import React from 'react';
import { useNavigate } from 'react-router-dom';

const BlogStanfordGenerativeAgents = () => {
  const navigate = useNavigate();
  return (
    <div className="bg-black pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-6">

        {/* Back button */}
        <button 
          onClick={() => navigate('/blog')} 
          className="mb-8 flex items-center text-gray-400 hover:text-white transition"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to Blog
        </button>

        {/* Blog post header */}
        <header className="mb-10">
          <h1 className="text-4xl font-extrabold text-white mb-4">
          Generative Agents Inspired HumanAI: How Stanford Simulated 1,000 Real People with AI
          </h1>
          <div className="flex items-center text-sm text-gray-400 mb-6">
            <span>July 2024</span>
            <span className="mx-2">•</span>
            <span>8 min read</span>
            <span className="mx-2">•</span>
            <span>Research Deep Dive</span>
          </div>
          <div className="flex items-center">
            <img 
              src={process.env.PUBLIC_URL + '/img/sharif-headshot.jpg'} 
              alt="Dr. Sharif Amlani" 
              className="h-10 w-10 rounded-full mr-3"
            />
            <div>
              <p className="text-white font-medium">Dr. Sharif Amlani</p>
              <p className="text-gray-400 text-sm">Founder & CEO, HumanAI</p>
            </div>
          </div>
        </header>

        {/* Blog content */}
        <div className="prose prose-lg prose-invert max-w-none space-y-6">

          <p className="text-lg">
            If you care about social science, market research, or the future of AI respondents, the latest paper out of Stanford is a must-read. The team built <strong>1,000+ generative agents</strong>—each simulating a real U.S. adult based on two-hour interviews—and then tested how well these agents could predict actual human responses to surveys, personality tests, and economic games.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-2">What did they do?</h2>
          <p>
            Researchers recruited a nationally representative sample (by age, race, gender, education, region, ideology) and conducted deep interviews with each participant. Then, for each person, they created an <strong>AI “twin”</strong> by injecting their entire interview transcript into a large language model (LLM) and instructing it to respond just like that individual.
          </p>
          <p>
            Both the humans and their AI twins completed the <strong>General Social Survey (GSS)</strong>, a 44-item Big Five personality inventory, behavioral economic games, and five classic experimental studies. Critically, the researchers measured not just “how close to average” the AI responses were, but whether they actually mirrored the *same* person's answers.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-2">What did they find?</h2>
          <ul>
            <li>
              <strong>AI twins matched their human counterparts with high accuracy.</strong> On the GSS, generative agents got the same answer as their human “source” 85% as often as the human gave the same answer themselves two weeks later.
            </li>
            <li>
              <strong>Richer interviews = better agents.</strong> Agents built on full interview data far outperformed those based only on demographic summaries or self-written bios.
            </li>
            <li>
              <strong>Bias reduction.</strong> Interview-based agents reduced performance disparities across race, ideology, and gender, compared to agents built only on demographic data.
            </li>
            <li>
              <strong>Strong population-level predictions.</strong> The effect sizes of experimental replications run on AI agents closely matched the results from the real human participants (correlation = 0.98).
            </li>
          </ul>

          <h2 className="text-2xl font-bold mt-8 mb-2">Why does it matter?</h2>
          <p>
            This work proves that <strong>AI agents can be used as stand-ins for real humans</strong> not by lazily slotting in stereotypes, but by building up a nuanced psychological and demographic profile from long-form interviews. For research, this means faster, cheaper, and more ethical simulation of human behavior—across everything from policy experiments to survey testing and product launches.
          </p>
          <p>
            And because these agents reduce demographic bias and match humans not just in the aggregate but at the individual level, they set a new benchmark for what AI-driven research tools should deliver.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-2">How does this connect to HumanAI?</h2>
          <p>
            At HumanAI, we’re inspired by this approach. Our mission is to build AI respondents that reflect <strong>how real people think, feel, and decide</strong>—using rich interviews, ongoing validation, and human-in-the-loop methods. The Stanford study proves this isn’t just science fiction—it’s possible, and it works.
          </p>

          <p className="text-lg font-semibold text-white">
            This is the future of research: diverse, accurate, bias-reduced AI agents, rigorously validated and ready for real-world testing.
          </p>

          <p className="mt-6 font-semibold">
            👉 <a href="#request-access" className="text-pink-400 underline hover:text-pink-300">Request early access</a> to see how HumanAI is taking these ideas into production.
          </p>

          <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 mt-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Ready to Try Synthetic Respondents?</h3>
                <p className="text-gray-300">Experience the future of market research with HumanAI's synthetic respondent technology.</p>
              </div>
              <a 
                href="https://www.humanaienterprise.com/auth" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-pink-600 hover:bg-pink-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 whitespace-nowrap"
              >
                Use It Free
              </a>
            </div>
          </div>

          <p className="mt-6 font-semibold">— Sharif From HumanAI</p>

        </div>
      </div>
    </div>
  );
};

export default BlogStanfordGenerativeAgents;
